import React, { useState, useEffect } from "react";
import { genSocial, saveSocial, listSocial, deleteSocial } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Share2, Sparkles, Copy, Save, Trash2, Loader2, Linkedin, Facebook, Instagram } from "lucide-react";

const NETWORKS = [
  { id: "linkedin", label: "LinkedIn", icon: Linkedin },
  { id: "facebook", label: "Facebook", icon: Facebook },
  { id: "instagram", label: "Instagram", icon: Instagram },
];

const SocialPanel = () => {
  const [network, setNetwork] = useState("linkedin");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("professionnel");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);
  const { toast } = useToast();

  const load = async () => {
    const r = await listSocial();
    setSaved(r.data);
  };
   
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const r = await genSocial({ network, topic, tone });
      setResult(r.data);
    } catch (err) {
      toast({ title: "Erreur", description: "Génération impossible", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copié ✓", description: "Collez-le sur votre réseau." });
  };

  const save = async () => {
    if (!result) return;
    await saveSocial({ network, topic, post: result.post, message: result.message });
    toast({ title: "Enregistré ✓" });
    load();
  };

  const remove = async (id) => {
    await deleteSocial(id);
    load();
  };

  const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-brand";

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Share2 size={20} className="text-brand" />
        <h2 className="font-heading font-bold text-xl text-neutral-900">Réseaux sociaux (IA)</h2>
      </div>
      <p className="text-sm text-neutral-500 mb-5">
        L'IA rédige vos posts et messages de prospection. Copiez-collez sur votre réseau (publication manuelle, conforme aux CGU).
      </p>

      <form onSubmit={generate} className="bg-white border border-neutral-200 rounded-xl p-5">
        <div className="flex flex-wrap gap-2 mb-4">
          {NETWORKS.map((n) => (
            <button type="button" key={n.id} onClick={() => setNetwork(n.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                network === n.id ? "bg-brand text-white border-brand" : "border-neutral-300 text-neutral-600 hover:border-brand"
              }`}>
              <n.icon size={16} /> {n.label}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-neutral-600 mb-1">Sujet / objectif</label>
            <input className={inputCls} placeholder="Ex: annoncer notre offre chantiers BTP" value={topic} onChange={(e) => setTopic(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-600 mb-1">Ton</label>
            <select className={inputCls} value={tone} onChange={(e) => setTone(e.target.value)}>
              <option value="professionnel">Professionnel</option>
              <option value="chaleureux">Chaleureux</option>
              <option value="dynamique">Dynamique</option>
              <option value="expert">Expert</option>
            </select>
          </div>
        </div>
        <button type="submit" disabled={loading} className="mt-4 flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold px-5 py-2.5 rounded-md disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {loading ? "Rédaction..." : "Générer"}
        </button>
      </form>

      {result && (
        <div className="mt-5 grid lg:grid-cols-2 gap-4">
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-neutral-800">Post à publier</h3>
              <button onClick={() => copy(result.post)} className="text-neutral-500 hover:text-brand"><Copy size={16} /></button>
            </div>
            <p className="text-sm text-neutral-700 whitespace-pre-wrap leading-relaxed">{result.post}</p>
          </div>
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-neutral-800">Message de prospection (DM)</h3>
              <button onClick={() => copy(result.message)} className="text-neutral-500 hover:text-brand"><Copy size={16} /></button>
            </div>
            <p className="text-sm text-neutral-700 whitespace-pre-wrap leading-relaxed">{result.message || "—"}</p>
          </div>
          <button onClick={save} className="lg:col-span-2 flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white font-semibold py-2.5 rounded-md">
            <Save size={16} /> Enregistrer ce contenu
          </button>
        </div>
      )}

      {saved.length > 0 && (
        <div className="mt-8">
          <h3 className="font-semibold text-neutral-800 mb-3">Contenus enregistrés</h3>
          <div className="space-y-3">
            {saved.map((s) => (
              <div key={s.id} className="bg-white border border-neutral-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="text-xs font-semibold text-brand uppercase">{s.network}</span>
                    {s.topic && <span className="text-xs text-neutral-500 ml-2">{s.topic}</span>}
                    <p className="text-sm text-neutral-700 mt-1 line-clamp-3 whitespace-pre-wrap">{s.post}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => copy(s.post)} className="text-neutral-400 hover:text-brand"><Copy size={15} /></button>
                    <button onClick={() => remove(s.id)} className="text-neutral-400 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialPanel;
